/**
 * PWA IndexedDB Database with Tenant Isolation
 * 
 * Using Dexie wrapper for IndexedDB with encryption support
 * Each tenant gets separate database for security isolation
 */

import Dexie, { Table } from 'dexie';
import { encryptForTenant, decryptForTenant, EncryptionError } from './encryption';
import { logger, captureMessage, buildTags, toSerializable } from '@/lib/monitoring/logger';

if (!Dexie.dependencies.indexedDB && typeof indexedDB !== 'undefined') {
  Dexie.dependencies.indexedDB = indexedDB;
}
if (!Dexie.dependencies.IDBKeyRange && typeof IDBKeyRange !== 'undefined') {
  Dexie.dependencies.IDBKeyRange = IDBKeyRange;
}

// Database schema version
export const DB_VERSION = 1;

// Record interfaces
export interface InstrumentSummary {
  id: string;
  tenantId: string;
  shortCode: string;
  assetTag?: string;
  encryptedData: ArrayBuffer; // Contains: name, serial, location, etc.
  version: number;
  lastSync: number;
  searchTokens: string[]; // For fast text search (unencrypted)
  syncStatus: 'pending' | 'synced' | 'conflict';
}

export interface CodeAlias {
  id: string;
  tenantId: string;
  namespace: 'legacy' | 'lab' | 'custom';
  alias: string;
  instrumentId: string;
  active: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface PendingMutation {
  id?: number; // Auto-increment primary key
  tenantId: string;
  idempotencyKey: string; // UUID for server-side deduplication
  type: 'status_update' | 'certificate_upload' | 'attachment' | 'usage_log';
  priority: 'high' | 'medium' | 'low';
  encryptedPayload: ArrayBuffer;
  timestamp: number;
  retries: number;
  maxRetries: number;
  nextRetry: number; // Exponential backoff timestamp
}

export interface TenantMetadata {
  key: string;
  tenantId: string;
  value: unknown;
  lastUpdated: number;
}

export type MutationType = PendingMutation['type'];
export type MutationPriority = PendingMutation['priority'];

interface BaseMutationPayload {
  instrumentId: string;
  [key: string]: unknown;
}

interface AttachmentMutationPayload extends BaseMutationPayload {
  attachmentId: string;
  metadata?: unknown;
}

type MutationPayloadMap = {
  status_update: BaseMutationPayload;
  certificate_upload: BaseMutationPayload;
  attachment: AttachmentMutationPayload;
  usage_log: BaseMutationPayload & {
    endpoint: string;
    method?: 'POST' | 'PUT' | 'PATCH';
    query?: Record<string, string | null | undefined>;
    body: Record<string, unknown>;
  };
};

export type MutationPayload<T extends MutationType = MutationType> = MutationPayloadMap[T];

// Decrypted instrument data interface
export interface InstrumentData {
  name: string;
  serial?: string;
  location?: string;
  manufacturer?: string;
  model?: string;
  calibrationInterval?: number;
  lastCalibrationDate?: string;
  nextCalibrationDate?: string;
  status: 'active' | 'inactive' | 'out_for_calibration' | 'overdue';
  notes?: string;
}

// Tenant-specific database class
export class TenantOfflineDB extends Dexie {
  instruments: Table<InstrumentSummary>;
  aliases: Table<CodeAlias>;
  mutations: Table<PendingMutation>;
  metadata: Table<TenantMetadata>;

  constructor(tenantId: string) {
    const dbName = `scopax_${tenantId}`;
    super(dbName);

    const deps = (this as unknown as {
      _deps: {
        indexedDB?: IDBFactory;
        IDBKeyRange?: typeof IDBKeyRange;
      };
    })._deps;
    const indexedDBFactory = typeof indexedDB !== 'undefined' ? indexedDB : Dexie.dependencies.indexedDB;
    const keyRangeFactory = typeof IDBKeyRange !== 'undefined' ? IDBKeyRange : Dexie.dependencies.IDBKeyRange;

    if (!deps.indexedDB && indexedDBFactory) {
      deps.indexedDB = indexedDBFactory;
    }
    if (!deps.IDBKeyRange && keyRangeFactory) {
      deps.IDBKeyRange = keyRangeFactory;
    }

    this.version(DB_VERSION).stores({
      // Instruments with compound indexes for performance
      instruments: 'id, tenantId, shortCode, assetTag, lastSync, syncStatus, [tenantId+syncStatus], [tenantId+lastSync], *searchTokens',
      
      // Code aliases for legacy support
      aliases: 'id, tenantId, alias, instrumentId, namespace, active, [tenantId+namespace], [tenantId+active]',
      
      // Mutations queue with priority and retry support
      mutations: '++id, tenantId, idempotencyKey, priority, timestamp, retries, nextRetry, [tenantId+priority], [priority+timestamp]',
      
      // Metadata for sync tracking
      metadata: 'key, tenantId, lastUpdated, [tenantId+lastUpdated]'
    });

    this.instruments = this.table('instruments');
    this.aliases = this.table('aliases');
    this.mutations = this.table('mutations');
    this.metadata = this.table('metadata');

    // Add hooks for automatic tenantId validation
    this.instruments.hook('creating', (primKey, obj) => {
      if (obj.tenantId !== tenantId) {
        throw new Error(`Tenant ID mismatch: expected ${tenantId}, got ${obj.tenantId}`);
      }
    });

    this.aliases.hook('creating', (primKey, obj) => {
      if (obj.tenantId !== tenantId) {
        throw new Error(`Tenant ID mismatch: expected ${tenantId}, got ${obj.tenantId}`);
      }
    });

    this.mutations.hook('creating', (primKey, obj) => {
      if (obj.tenantId !== tenantId) {
        throw new Error(`Tenant ID mismatch: expected ${tenantId}, got ${obj.tenantId}`);
      }
    });

    this.metadata.hook('creating', (primKey, obj) => {
      if (obj.tenantId !== tenantId) {
        throw new Error(`Tenant ID mismatch: expected ${tenantId}, got ${obj.tenantId}`);
      }
    });
  }
}

// Database manager for handling multiple tenants
export class DatabaseManager {
  private databases = new Map<string, TenantOfflineDB>();
  private currentTenantId: string | null = null;
  private currentUserId: string | null = null;

  /**
   * Get database for specific tenant
   */
  async getDatabase(tenantId: string): Promise<TenantOfflineDB> {
    if (!this.databases.has(tenantId)) {
      let db = new TenantOfflineDB(tenantId);

      try {
        await db.open();
        this.databases.set(tenantId, db);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        const isSchemaMismatch = message.includes('Table') && message.includes('instruments');

        if (isSchemaMismatch) {
          try {
            await db.delete();
            db = new TenantOfflineDB(tenantId);
            await db.open();
            this.databases.set(tenantId, db);
          } catch (reinitializationError) {
            logger.error('Failed to reset offline database for tenant', reinitializationError, {
              extra: { tenantId },
            });
            throw new Error(`Database reinitialization failed for tenant ${tenantId}`);
          }
        } else {
          logger.error('Failed to open database for tenant', error, { extra: { tenantId } });
          throw new Error(`Database initialization failed for tenant ${tenantId}`);
        }
      }
    }

    return this.databases.get(tenantId)!;
  }

  /**
   * Set current tenant and user context
   */
  setContext(tenantId: string, userId: string): void {
    this.currentTenantId = tenantId;
    this.currentUserId = userId;
  }

  /**
   * Get current database (requires context to be set)
   */
  async getCurrentDatabase(): Promise<TenantOfflineDB> {
    if (!this.currentTenantId) {
      throw new Error('No tenant context set');
    }
    return this.getDatabase(this.currentTenantId);
  }

  /**
   * Store encrypted instrument data
   */
  async storeInstrument(
    instrumentId: string,
    instrumentData: InstrumentData,
    shortCode: string,
    assetTag?: string
  ): Promise<void> {
    if (!this.currentTenantId || !this.currentUserId) {
      throw new Error('No tenant/user context set');
    }

    try {
      // Encrypt instrument data
      const encryptedData = await encryptForTenant(
        instrumentData,
        this.currentTenantId,
        this.currentUserId
      );

      // Generate search tokens from unencrypted fields
      const searchTokens = this.generateSearchTokens(instrumentData);

      const instrument: InstrumentSummary = {
        id: instrumentId,
        tenantId: this.currentTenantId,
        shortCode,
        assetTag,
        encryptedData,
        version: 1,
        lastSync: Date.now(),
        searchTokens,
        syncStatus: 'synced'
      };

      const db = await this.getCurrentDatabase();
      await db.instruments.put(instrument);
    } catch (error) {
      logger.error('Failed to store instrument', error, {
        extra: { instrumentId, tenantId: this.currentTenantId },
      });
      throw error;
    }
  }

  /**
   * Retrieve and decrypt instrument data
   */
  async getInstrument(instrumentId: string): Promise<InstrumentData | null> {
    if (!this.currentTenantId || !this.currentUserId) {
      throw new Error('No tenant/user context set');
    }

    try {
      const db = await this.getCurrentDatabase();
      const instrument = await db.instruments.get(instrumentId);

      if (!instrument) {
        return null;
      }

      // Decrypt instrument data
      const instrumentData = await decryptForTenant<InstrumentData>(
        instrument.encryptedData,
        this.currentTenantId,
        this.currentUserId
      );

      return instrumentData;
    } catch (error) {
      logger.error('Failed to get instrument', error, {
        extra: { instrumentId, tenantId: this.currentTenantId },
      });
      if (error instanceof EncryptionError) {
        void captureMessage('pwa.database.instrument.decrypt_failed', 'warning', {
          tags: buildTags('pwa-database', 'decrypt'),
          extra: toSerializable({
            instrumentId,
            tenantId: this.currentTenantId
          })
        });
        return null;
      }
      throw error;
    }
  }

  /**
   * Find instrument by short code (for QR scanning)
   */
  async findInstrumentByShortCode(shortCode: string): Promise<InstrumentData | null> {
    if (!this.currentTenantId || !this.currentUserId) {
      throw new Error('No tenant/user context set');
    }

    try {
      const db = await this.getCurrentDatabase();
      const instrument = await db.instruments.where('shortCode').equals(shortCode).first();

      if (!instrument) {
        return null;
      }

      // Decrypt instrument data
      const instrumentData = await decryptForTenant<InstrumentData>(
        instrument.encryptedData,
        this.currentTenantId,
        this.currentUserId
      );

      return instrumentData;
    } catch (error) {
      logger.error('Failed to find instrument by short code', error, {
        extra: { shortCode, tenantId: this.currentTenantId },
      });
      return null;
    }
  }

  /**
   * Find instrument by asset tag (for legacy scanning)
   */
  async findInstrumentByAssetTag(assetTag: string): Promise<InstrumentData | null> {
    if (!this.currentTenantId || !this.currentUserId) {
      throw new Error('No tenant/user context set');
    }

    try {
      const db = await this.getCurrentDatabase();
      const instrument = await db.instruments.where('assetTag').equals(assetTag).first();

      if (!instrument) {
        return null;
      }

      // Decrypt instrument data
      const instrumentData = await decryptForTenant<InstrumentData>(
        instrument.encryptedData,
        this.currentTenantId,
        this.currentUserId
      );

      return instrumentData;
    } catch (error) {
      logger.error('Failed to find instrument by asset tag', error, {
        extra: { assetTag, tenantId: this.currentTenantId },
      });
      return null;
    }
  }

  /**
   * Find instrument by alias (for code resolution)
   */
  async findInstrumentByAlias(alias: string): Promise<InstrumentData | null> {
    if (!this.currentTenantId || !this.currentUserId) {
      throw new Error('No tenant/user context set');
    }

    try {
      const db = await this.getCurrentDatabase();
      
      // First find the alias
      const codeAlias = await db.aliases
        .where('alias')
        .equals(alias)
        .and(a => a.active)
        .first();

      if (!codeAlias) {
        return null;
      }

      // Then get the instrument
      return this.getInstrument(codeAlias.instrumentId);
    } catch (error) {
      logger.error('Failed to find instrument by alias', error, {
        extra: { alias, tenantId: this.currentTenantId },
      });
      return null;
    }
  }

  /**
   * Upsert code aliases used for QR/offline resolution
   */
  async upsertAliases(aliases: CodeAlias[]): Promise<void> {
    if (!this.currentTenantId) {
      throw new Error('No tenant context set');
    }

    if (!aliases.length) {
      return;
    }

    try {
      const tenantId = this.currentTenantId;
      const db = await this.getCurrentDatabase();

      const payload = aliases.map((alias) => ({
        ...alias,
        tenantId
      }));

      await db.aliases.bulkPut(payload);
    } catch (error) {
      logger.error('Failed to upsert aliases', error, {
        extra: { count: aliases.length, tenantId: this.currentTenantId },
      });
      throw error;
    }
  }

  /**
   * Queue a mutation for background sync
   */
  async queueMutation<T extends MutationType>(
    type: T,
    payload: MutationPayload<T>,
    priority: MutationPriority = 'medium',
    idempotencyKey?: string
  ): Promise<void> {
    if (!this.currentTenantId || !this.currentUserId) {
      throw new Error('No tenant/user context set');
    }

    try {
      // Generate idempotency key if not provided
      const mutationId = idempotencyKey || crypto.randomUUID();

      // Encrypt payload
      const encryptedPayload = await encryptForTenant(
        payload,
        this.currentTenantId,
        this.currentUserId
      );

      const mutation: PendingMutation = {
        tenantId: this.currentTenantId,
        idempotencyKey: mutationId,
        type,
        priority,
        encryptedPayload,
        timestamp: Date.now(),
        retries: 0,
        maxRetries: 3,
        nextRetry: Date.now() // Can retry immediately
      };

      const db = await this.getCurrentDatabase();
      await db.mutations.add(mutation);
    } catch (error) {
      logger.error('Failed to queue mutation', error, {
        extra: { type, priority, tenantId: this.currentTenantId },
      });
      throw error;
    }
  }

  /**
   * Get pending mutations for sync
   */
  async getPendingMutations(): Promise<PendingMutation[]> {
    if (!this.currentTenantId) {
      throw new Error('No tenant context set');
    }

    try {
      const db = await this.getCurrentDatabase();
      const now = Date.now();

      // Get mutations that are ready to retry
      return await db.mutations
        .where('nextRetry')
        .belowOrEqual(now)
        .and(m => m.retries < m.maxRetries)
        .sortBy('priority'); // High priority first
    } catch (error) {
      logger.error('Failed to get pending mutations', error, { extra: { tenantId: this.currentTenantId } });
      return [];
    }
  }

  /**
   * Mark mutation as completed
   */
  async completeMutation(idempotencyKey: string): Promise<void> {
    if (!this.currentTenantId) {
      throw new Error('No tenant context set');
    }

    try {
      const db = await this.getCurrentDatabase();
      await db.mutations.where('idempotencyKey').equals(idempotencyKey).delete();
    } catch (error) {
      logger.error('Failed to complete mutation', error, {
        extra: { idempotencyKey, tenantId: this.currentTenantId },
      });
      throw error;
    }
  }

  /**
   * Increment mutation retry count with exponential backoff
   */
  async retryMutation(idempotencyKey: string): Promise<void> {
    if (!this.currentTenantId) {
      throw new Error('No tenant context set');
    }

    try {
      const db = await this.getCurrentDatabase();
      const mutation = await db.mutations.where('idempotencyKey').equals(idempotencyKey).first();

      if (!mutation) {
        return;
      }

      const newRetries = mutation.retries + 1;
      const backoffMs = Math.min(1000 * Math.pow(2, newRetries), 30000); // Max 30 seconds
      const jitter = Math.random() * 0.1 * backoffMs; // 10% jitter
      const nextRetry = Date.now() + backoffMs + jitter;

      await db.mutations.where('idempotencyKey').equals(idempotencyKey).modify({
        retries: newRetries,
        nextRetry
      });
    } catch (error) {
      logger.error('Failed to retry mutation', error, {
        extra: { idempotencyKey, tenantId: this.currentTenantId },
      });
      throw error;
    }
  }

  /**
   * Clear all data for tenant (on tenant switch)
   */
  async clearTenantData(tenantId: string): Promise<void> {
    try {
      const db = await this.getDatabase(tenantId);
      await db.transaction('rw', [db.instruments, db.aliases, db.mutations, db.metadata], async () => {
        await db.instruments.clear();
        await db.aliases.clear();
        await db.mutations.clear();
        await db.metadata.clear();
      });

      // Close and remove database reference
      db.close();
      this.databases.delete(tenantId);
    } catch (error) {
      logger.error('Failed to clear tenant data', error, { extra: { tenantId } });
      throw error;
    }
  }

  /**
   * Get storage usage estimate
   */
  async getStorageEstimate(): Promise<StorageEstimate | null> {
    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        return await navigator.storage.estimate();
      }
      return null;
    } catch (error) {
      logger.error('Failed to get storage estimate', error);
      return null;
    }
  }

  /**
   * Expose current tenant context for worker scheduling and telemetry
   */
  getActiveTenantId(): string | null {
    return this.currentTenantId;
  }

  /**
   * Generate search tokens from instrument data
   */
  private generateSearchTokens(instrumentData: InstrumentData): string[] {
    const tokens: string[] = [];
    
    // Add searchable fields (unencrypted for performance)
    if (instrumentData.name) {
      tokens.push(...instrumentData.name.toLowerCase().split(/\s+/));
    }
    if (instrumentData.manufacturer) {
      tokens.push(instrumentData.manufacturer.toLowerCase());
    }
    if (instrumentData.model) {
      tokens.push(instrumentData.model.toLowerCase());
    }
    if (instrumentData.serial) {
      tokens.push(instrumentData.serial.toLowerCase());
    }
    
    // Remove duplicates and filter out short tokens
    return [...new Set(tokens)].filter(token => token.length > 2);
  }

  /**
   * Close all databases
   */
  async closeAll(): Promise<void> {
    for (const [tenantId, db] of this.databases) {
      try {
        db.close();
      } catch (error) {
        logger.error('Failed to close database for tenant', error, { extra: { tenantId } });
      }
    }
    this.databases.clear();
    this.currentTenantId = null;
    this.currentUserId = null;
  }
}

// Global database manager instance
export const dbManager = new DatabaseManager();

// Utility functions
export async function initializeDatabase(tenantId: string, userId: string): Promise<void> {
  dbManager.setContext(tenantId, userId);
  await dbManager.getCurrentDatabase();
}

export async function switchTenant(newTenantId: string, userId: string, clearOldData = true): Promise<void> {
  const oldTenantId = dbManager['currentTenantId'];
  
  if (oldTenantId && clearOldData) {
    await dbManager.clearTenantData(oldTenantId);
  }
  
  dbManager.setContext(newTenantId, userId);
}
